import { useMemo } from 'react';
import {
    Badge, CardFooter, Col, ListGroup, ListGroupItem, ListGroupItemHeading, ListGroupItemText, Row,
} from 'reactstrap';
import { isEmpty } from 'lodash-es';
import { Link } from '@s20/components/common/Link';
import { crewLeadLabel } from '@s20/i18n';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getTenantSettings } from '@s20/selectors/features/common.selectors';
import { getMapUrl } from '@s20/common/util/dom';
import { getShort } from '@s20/common/util/date';
import { AppNavPaths } from '../../../Common';
import { INTERPUNCT } from '../../../common/util';

export const Job = ({ ...job }) => {
    const tenantSettings = useSelector(getTenantSettings);

    const resources = useMemo(() => {
        // We get the assigned equipment as a string, so for now we'll need to get each part to loop over it
        const equipmentArray = (job.assignedEquipment || '').split('\n').map((line) => {
            // Thank you, Claude, for this gibberish
            const match = line.match(/^(.+?)\s*\(([^:]+):\s*(.+)\)$/);

            if (!match) {
                return null;
            }

            const [, identifier, type, employeeName] = match;

            return {
                identifier: identifier.trim(),
                type: type.trim(),
                employeeName: employeeName.trim(),
            };
        }).filter(Boolean);

        return equipmentArray;
    }, [job.assignedEquipment]);

    return (
        <>
            <div className="p-2">
                <Row className="mb-2">
                    <Col>
                        <Link
                            to={
                                job.projectId
                                    ? `${AppNavPaths.Project}/${job.projectId}?openJob=${job.jobId}`
                                    : `${AppNavPaths.Job}/${job.jobId}`
                            }
                            target="_blank"
                            className="site-link h5 mb-0"
                        >
                            {job.jobNumber}
                        </Link>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col xs="12">
                        <div className="text-muted text-uppercase small">
                            Job Date
                        </div>

                        <div className="font-weight-semibold">
                            {getShort(job.assignmentDate) || '-'}
                        </div>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col xs="12">
                        <div className="text-muted text-uppercase small">
                            Customer
                        </div>

                        <div className="font-weight-semibold">
                            {job.customer || '-'}
                        </div>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col xs="12">
                        <div className="text-muted text-uppercase small">
                            Location
                        </div>

                        <div className="font-weight-semibold text-break">
                            <a
                                href={getMapUrl(job.jobLocationAddress)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="site-link"
                            >
                                {job.jobLocationAddress || '-'}
                            </a>
                        </div>
                    </Col>
                </Row>

                {(job.crewLead || job.startTime) && (
                    <Row className="mb-2">
                        {job.crewLead && (
                            <Col xs={job.startTime ? '6' : '12'}>
                                <div className="text-muted text-uppercase small">
                                    {crewLeadLabel(tenantSettings)}
                                </div>
                                <div className="font-weight-semibold">
                                    {job.crewLead}
                                </div>
                            </Col>
                        )}

                        {job.startTime && (
                            <Col xs={job.crewLead ? '6' : '12'}>
                                <div className="text-muted text-uppercase small">
                                    Start Time
                                </div>
                                <div className="font-weight-semibold">
                                    {job.startTime}
                                </div>
                            </Col>
                        )}
                    </Row>
                )}

                {!isEmpty(resources) && (
                    <>
                        <div className="text-muted text-uppercase small">Equipment</div>

                        <ListGroup>
                            {resources.map((equipment, index) => (
                                // The same identifier can be listed more than once, so the
                                // position in the parsed list is what makes the key unique.
                                <ListGroupItem key={`${equipment.identifier}-${index}`}>
                                    <ListGroupItemHeading>{equipment.type}</ListGroupItemHeading>

                                    <ListGroupItemText className="mb-0">
                                        {equipment.identifier}

                                        {Boolean(equipment.employeeName) && (
                                            <>
                                                {` ${INTERPUNCT} `}

                                                {equipment.employeeName}
                                            </>
                                        )}
                                    </ListGroupItemText>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </>
                )}
            </div>

            <CardFooter
                tag="footer"
                className="border-0"
            >
                <Row>
                    <Col
                        xs="6"
                        className="text-center"
                    >
                        <span>Required</span>

                        <div>
                            <Badge
                                color="secondary"
                                className="px-2"
                            >
                                {job.requiredResources || 0}
                            </Badge>
                        </div>
                    </Col>
                    <Col
                        xs="6"
                        className="text-center"
                    >
                        <span>Assigned</span>
                        <div>
                            <Badge
                                color={(job.assignmentCount || 0) >= (job.requiredResources || 0) ? 'success' : 'warning'}
                                className="px-2"
                            >
                                {job.assignmentCount || 0}
                            </Badge>
                        </div>
                    </Col>
                </Row>

            </CardFooter>
        </>
    );
};